import React, { useEffect, useState } from 'react'

import { set } from 'lodash'
import { ChannelCreationFormErrorState, ChannelDetailsType } from '../../../lib/types/settings'
import useSWR from 'swr'
import { alertSeverites, apiURI } from 'src/lib/utils/constants'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import { parseChannelDetails } from 'src/lib/utils/parsers'
import { useRouter } from 'next/router'
import FormTile from 'src/components/common/containers/FormTile'
import ChannelCreationForm from './ChannelCreationForm'
import { initialFormErrorState } from 'src/assets/SettingsConfig'
import { ChevronDown, Info } from 'react-feather'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { getDateTime } from 'src/lib/utils/helpers'
import SaveNotification from 'src/components/common/SaveNotification'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { deleteChannel, updateChannelDetails } from 'src/lib/core/api-methods'
import { t } from 'i18next'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import RowMenu from 'src/components/common/form-elements/RowMenu'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import { Button } from '@mui/material'
import Loader from 'src/components/common/loader-components/Loader'

import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'

interface ChannelDetailsProps {
  channelId: string | string[] | undefined
}
const ViewChannelDetails: React.FC<ChannelDetailsProps> = React.memo(({ channelId }) => {
  const router = useRouter()
  const { setLoaderProperties } = useLoader()

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false)
  const [channelDetails, setChannelDetails] = useState<ChannelDetailsType>()
  const [formErrors, setFormErrors] = useState<ChannelCreationFormErrorState>(initialFormErrorState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [showSaveStrip, setShowSaveStrip] = useState<boolean>(false)
  const [isTextCopied, setIsTextCopied] = useState<boolean>(false)

  const { sendActivityLog } = useActivityLogs()

  const shouldFetchChannelResponse = () => {
    return Boolean(Object.keys(router.query).length <= 2)
  }

  useEffect(() => {
    if (!shouldFetchChannelResponse()) setChannelDetails(parseChannelDetailsFromQueryParams())
  }, [])

  const parseChannelDetailsFromQueryParams = () => {
    const { apiKey, dateCreated, description, name, s_id } = router.query
    return {
      apiKey: apiKey as string,
      dateCreated: dateCreated as string,
      description: description as string,
      name: name as string,
      s_id: s_id as string
    }
  }

  const { data: channelResponse, isValidating } = useSWR(
    shouldFetchChannelResponse() ? [`/merchant-app/${channelId}`, apiURI.AUTH_ENGINE] : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  useEffect(() => {
    if (shouldFetchChannelResponse()) {
      if (!Boolean(channelResponse) || Boolean(isValidating)) return
    }
    setShowSaveStrip(Boolean(JSON.stringify(channelDetails) !== JSON.stringify(initChannelDetails())))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelDetails])

  useEffect(() => {
    if (!Boolean(channelResponse) || Boolean(isValidating)) return
    setChannelDetails(parseChannelDetails((channelResponse as any)?.data))
  }, [channelResponse, isValidating])

  const initChannelDetails = () => {
    return shouldFetchChannelResponse()
      ? parseChannelDetails((channelResponse as any)?.data)
      : parseChannelDetailsFromQueryParams()
  }

  const handleResetChannelDetails = () => {
    const initSettings = initChannelDetails()
    setChannelDetails(initSettings)
    setShowSaveStrip(false)
  }

  const isValidForm = () => {
    let errorFound = false
    if (!Boolean(channelDetails?.name?.length)) {
      setFormErrors((oldValues: ChannelCreationFormErrorState) => ({
        ...oldValues,
        ...{ name: { status: true, errorCode: '' } }
      }))
      errorFound = true
    }
    return !errorFound
  }

  const handleUpdateChannelDetails = async () => {
    if (!isValidForm()) return
    const updateChannelPayload = {
      name: channelDetails?.name,
      description: channelDetails?.description
    }
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const channelUpdateResponse: any = await updateChannelDetails(channelDetails?.s_id ?? '', updateChannelPayload)
      if (channelUpdateResponse?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, channelUpdateResponse?.error)
        console.error('x-trace-id:', channelUpdateResponse?.headers?.['x-trace-id'])
      }
      setLoaderProperties({ isLoaderVisible: false })
      handleAlerts(alertSeverites.SEVERITY_SUCCESS, t('changes_saved'))
      setShowSaveStrip(false)
      const activityLogPayload = {
        entityName: EntityName.SETTINGS,
        actionName: 'edited Setup'
      }
      sendActivityLog(activityLogPayload)
    } catch (e) {
      setLoaderProperties({ isLoaderVisible: false })
      console.error(e)
      handleAlerts(alertSeverites.SEVERITY_ERROR, t('something_went_wrong'))
    }
  }

  const handleDeleteChannel = async () => {
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const channelDeleteResponse: any = await deleteChannel(channelDetails?.s_id ?? '')
      if (channelDeleteResponse?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, channelDeleteResponse?.error)
        console.error('x-trace-id:', channelDeleteResponse?.headers?.['x-trace-id'])
      }
      //Sending ActivityLog
      const activityLogPayload = {
        entityName: EntityName.SETTINGS,
        actionName: 'edited Setup'
      }
      sendActivityLog(activityLogPayload)
      setLoaderProperties({ isLoaderVisible: false })
      router.back()
    } catch (e) {
      setLoaderProperties({ isLoaderVisible: false })
      console.error(e)
      handleAlerts(alertSeverites.SEVERITY_ERROR, t('something_went_wrong'))
    }
  }

  // ** Alert Handlers ** //
  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <div className='absolute top-[94px] right-[60px] flex gap-1'>
        <RowMenu
          label={'Actions'}
          icon={<ChevronDown className='w-4 h-4 stroke-[2.5px] text-coal-dark' />}
          menuData={[
            {
              itemID: 'delete',
              handleClick: () => {
                setIsDeleteConfirmationOpen(true)
              },
              component: <span className='text-sm text-coal-dark'> Delete this channel</span>
            }
          ]}
        />
      </div>

      <div className='flex flex-col gap-4'>
        <FormTile title='Channel information'>
          <ChannelCreationForm
            channelDetails={channelDetails}
            setChannelDetails={setChannelDetails}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
        </FormTile>
        <FormTile title='API access token'>
          {isValidating ? (
            <Loader />
          ) : (
            <div className='flex flex-col gap-4'>
              <span className='text-carbon text-sm'>Use this to call Shopflo checkout and other services</span>
              <div className='flex gap-2 items-start border border-[#C7981F] rounded-lg p-3'>
                <Info className='w-4 h-4 text-[#C7981F]' />
                <span className='text-sm text-carbon leading-4'>
                  {channelDetails?.isTokenRevealed
                    ? 'You can’t view this again because it provides authenticated access to your channel.'
                    : 'To protect your data, you’ll only be able to reveal your API token once. Copy and save your API access token in a secure place.'}
                </span>
              </div>
              <div className='flex flex-col gap-1'>
                <div className='flex'>
                  <span
                    className={`bg-[#f5f5f5] rounded-lg px-3 py-2 w-full border ${
                      channelDetails?.isTokenRevealed ? '' : 'rounded-r-none border-r-0'
                    }`}
                  >
                    {channelDetails?.apiKey}
                  </span>
                  {!Boolean(channelDetails?.isTokenRevealed) ? (
                    <Button
                      variant='outlined'
                      sx={{ borderleft: '0px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                      onClick={() => {
                        navigator.clipboard.writeText(channelDetails?.apiKey ?? '')
                        setIsTextCopied(true)
                      }}
                    >
                      {isTextCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  ) : (
                    <></>
                  )}
                </div>
                <span className='text-sm text-coal-light'>
                  Created {getDateTime(new Date(parseInt(channelDetails?.dateCreated ?? '0')))?.date} at{' '}
                  {getDateTime(new Date(parseInt(channelDetails?.dateCreated ?? '0')))?.time} IST
                </span>
              </div>
            </div>
          )}
        </FormTile>
      </div>
      <SaveNotification
        onSave={handleUpdateChannelDetails}
        onDiscard={handleResetChannelDetails}
        isVisible={showSaveStrip}
      />
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={5000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
      {isDeleteConfirmationOpen ? (
        <ConfirmationDialog
          jobButton={'Delete channel'}
          cancelButton={'Cancel'}
          jobButtonColor='error'
          cancelButtonColor='secondary'
          confirmationText={'Are you sure you want to delete this channel?'}
          title={'Delete channel'}
          isOpen={isDeleteConfirmationOpen}
          setIsConfirmationOpen={setIsDeleteConfirmationOpen}
          handleJob={() => handleDeleteChannel()}
        />
      ) : (
        <></>
      )}
    </>
  )
})

export default ViewChannelDetails
